import { useEffect, useState } from 'react'
import { Phone, CalendarClock, AlertTriangle, CheckCircle2, XCircle, ExternalLink, Plane, BarChart3, Mail, Headphones } from 'lucide-react'
import { useDepartments, useDashboardDept } from '../../hooks/useDashboardDept'

function fmtTime(iso: string) { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }

// OSPROD-224: leave-type chip styling + initials fallback for the out-of-office block.
const LEAVE_CHIP: Record<string, string> = {
  PTO: 'bg-emerald-500/15 text-emerald-500',
  SICK: 'bg-amber-500/15 text-amber-500',
  OTHER: 'bg-slate-400/15 text-slate-400',
}
const LEAVE_LABEL: Record<string, string> = { PTO: 'PTO', SICK: 'Sick', OTHER: 'Other' }
function initials(name: string): string {
  return (name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('') || '?').toUpperCase()
}

export default function DepartmentDashboard() {
  const departments = useDepartments()
  const [deptId, setDeptId] = useState<string | null>(null)
  const [range, setRange] = useState<'today' | 'week' | 'month'>('week')
  useEffect(() => { if (!deptId && departments.length) setDeptId(departments[0].id) }, [departments, deptId])
  const { snapshot, loading } = useDashboardDept(deptId, range)

  if (departments.length === 0) {
    return <div className="flex-1 p-8 text-sm text-muted-foreground">You don't head any department yet.</div>
  }

  return (
    <div className="flex-1 min-w-0 h-full overflow-y-auto bg-background text-foreground">
      <div className="max-w-5xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Department</h1>
          <select
            value={deptId ?? ''}
            onChange={(e) => setDeptId(e.target.value)}
            className="text-sm rounded-lg bg-muted border border-border px-3 py-1.5"
          >
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <div className="ml-auto flex rounded-lg border border-border overflow-hidden text-xs">
            {(['today', 'week', 'month'] as const).map((w) => (
              <button
                key={w}
                onClick={() => setRange(w)}
                className={`px-3 py-1.5 ${range === w ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {w === 'today' ? 'Today' : w === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="h-24 rounded-xl bg-card animate-pulse" />}

        {snapshot && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Overdue" value={snapshot.kpis.overdue} tone="text-rose-400" />
              <Kpi label="Due today" value={snapshot.kpis.dueToday} />
              <Kpi label="Done today" value={snapshot.kpis.doneToday} tone="text-emerald-400" />
              <Kpi label="Events today" value={snapshot.kpis.eventsToday} />
            </div>

            {snapshot.workload?.length > 0 && (
              <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
                  <BarChart3 size={13} className="text-muted-foreground" /> Workload
                  <span className="text-muted-foreground normal-case">· {range === 'today' ? 'today' : range === 'month' ? 'this month' : 'this week'}</span>
                </div>
                {snapshot.workload.map((m) => (
                  <div key={m.userId} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/40 last:border-0">
                    <span className="w-6 h-6 rounded-full bg-primary/80 text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0">{m.initials}</span>
                    <span className="flex items-center gap-1.5 w-28 shrink-0 text-sm truncate">
                      {m.present && <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" title="In a voice channel now" />}
                      {m.name}
                    </span>
                    <span className="flex-1 h-2 rounded-full bg-muted overflow-hidden" title={`${m.load}% load`}>
                      <span className={`block h-full rounded-full ${m.load < 25 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${m.load}%` }} />
                    </span>
                    <span className="flex items-center gap-3 text-[11px] text-muted-foreground tabular-nums shrink-0">
                      <span className="flex items-center gap-1" title="Tasks done"><CheckCircle2 size={12} className="text-emerald-400" />{m.done}</span>
                      <span className="flex items-center gap-1" title="Calls"><Phone size={12} />{m.calls}</span>
                      <span className="flex items-center gap-1" title="Meetings"><CalendarClock size={12} />{m.meetings}</span>
                      <span className="flex items-center gap-1" title="Replies"><Mail size={12} />{m.replies}</span>
                      <span className="flex items-center gap-1" title="Voice time"><Headphones size={12} />{Math.round(m.voiceMin / 6) / 10}h</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* OSPROD-224: who on the team is out today (approved PTO / sick). */}
            {snapshot.onLeave?.length > 0 && (
              <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
                  <Plane size={13} className="text-muted-foreground" /> Out of office today
                  <span className="text-muted-foreground normal-case">({snapshot.onLeave.length})</span>
                </div>
                {snapshot.onLeave.map((p) => (
                  <div key={p.userId} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/40 last:border-0">
                    {p.avatarUrl ? (
                      <img src={p.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center shrink-0">{initials(p.name)}</span>
                    )}
                    <span className="flex-1 text-sm truncate">{p.name}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${LEAVE_CHIP[p.type] ?? 'bg-muted text-muted-foreground'}`}>{LEAVE_LABEL[p.type] ?? p.type}</span>
                    <span className="text-[11px] text-muted-foreground/70 w-32 text-right shrink-0">{p.startDate} – {p.endDate}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-foreground uppercase tracking-wide">Team status</div>
              {snapshot.members.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground/70">No members in this department.</div>}
              {snapshot.members.map((m) => (
                <div key={m.userId} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/40 last:border-0">
                  <span className="w-6 h-6 rounded-full bg-primary/80 text-primary-foreground text-[10px] font-bold flex items-center justify-center">{m.initials}</span>
                  <span className="flex items-center gap-1.5 flex-1 text-sm truncate">
                    {m.present && <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" title="In a voice channel now" />}
                    {m.name}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-rose-400 w-16 justify-end" title="Overdue tasks"><AlertTriangle size={12} />{m.overdue}</span>
                  <span className="text-[11px] text-muted-foreground w-14 text-right" title="Due today">{m.dueToday} due</span>
                  <span className="flex items-center gap-1 text-[11px] text-emerald-400 w-14 justify-end" title="Done today"><CheckCircle2 size={12} />{m.doneToday}</span>
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground w-12 justify-end" title="Calls today"><Phone size={12} />{m.callsToday}</span>
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground w-12 justify-end" title="Meetings today"><CalendarClock size={12} />{m.meetingsToday}</span>
                </div>
              ))}
            </div>

            {/* OSPROD-219: failed / QA-failed tasks for the daily-call discussion. */}
            <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
                <XCircle size={13} className="text-rose-400" /> Failed / QA tasks
                {snapshot.failedTasks?.length ? <span className="text-rose-400 normal-case">({snapshot.failedTasks.length})</span> : null}
              </div>
              {(!snapshot.failedTasks || snapshot.failedTasks.length === 0) && (
                <div className="px-4 py-6 text-sm text-muted-foreground/70">No failed tasks right now.</div>
              )}
              {snapshot.failedTasks?.map((t) => (
                <div key={t.itemId} className="flex items-start gap-3 px-4 py-2.5 border-b border-border/40 last:border-0">
                  <span className="mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-400 shrink-0">{t.statusName}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      {t.externalUrl ? (
                        <a href={t.externalUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                          {t.externalKey ? `${t.externalKey} · ` : ''}{t.title}<ExternalLink size={11} className="shrink-0" />
                        </a>
                      ) : (
                        <span>{t.title}</span>
                      )}
                    </div>
                    {t.reason && <div className="text-[11px] text-muted-foreground/80 truncate" title={t.reason}>{t.reason}</div>}
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0 w-24 text-right truncate" title="Assignee">{t.assigneeName ?? 'Unassigned'}</span>
                  {t.ageDays != null && <span className="text-[11px] text-muted-foreground/70 shrink-0 w-12 text-right" title="Days in failed status">{t.ageDays}d</span>}
                </div>
              ))}
            </div>

            <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-foreground uppercase tracking-wide">Recent department calls</div>
              {snapshot.callDigest.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground/70">No recent calls.</div>}
              {snapshot.callDigest.map((c) => (
                <div key={c.sessionId} className="flex items-center gap-3 px-4 py-2 border-b border-border/40 last:border-0 text-sm">
                  <Phone size={14} className="text-foreground/60" />
                  <span className="flex-1 truncate">{c.title}</span>
                  <span className="text-[11px] text-muted-foreground">{c.participants} ppl</span>
                  <span className="text-[11px] text-muted-foreground w-16 text-right">{c.durationMin != null ? `${c.durationMin} min` : '—'}</span>
                  <span className="text-[11px] text-muted-foreground/70 w-28 text-right">{fmtTime(c.startedAt)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-xl bg-card ring-1 ring-border/40 px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums leading-tight ${tone ?? ''}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}
